import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Mediator } from '@spotahome/soyuz-mediator';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { Button } from '@spotahome/ui-library';

import {
  QUICK_ALERT_STORAGE_DATA,
  createAlert,
  getAlertFromFilters,
  getAlertDataFromStorage
} from '../utils/alerts';
import SoyuzLocalStorage from '../utils/soyuzLocalStorage';

import { COMMON_LOGIN_MODAL } from '../channels';

const IS_QUICK_ALERT_CREATION = true;

const triggerOpenModal = isSignupContextualized => {
  Mediator.publish(COMMON_LOGIN_MODAL, {
    initialAction: 'signup',
    renderTitle: action =>
      action === 'signup' && isSignupContextualized
        ? trans('contextualized.signup.alert.title')
        : trans(`${action}.modal.title`),
    renderSubtitle: action =>
      action === 'signup' && isSignupContextualized
        ? trans('contextualized.signup.alert.subtitle')
        : trans(`${action}.modal.subtitle`),
    events: {
      open: {
        eventCategory: 'signup/login modal',
        eventAction: 'Alerts',
        eventLabel: 'open'
      },
      close: {
        eventCategory: 'signup/login modal',
        eventAction: 'Alerts',
        eventLabel: 'close'
      }
    }
  });
};

const AlertButton = ({
  expandedFilters,
  cityId,
  filters,
  isUserLogged,
  onClick,
  buttonClassName,
  buttonProps,
  customText,
  dataTest
}) => {
  const signupContextualizedInfoExperiment = useSoyuzExperiment(
    'SignupContextualizedInfo'
  );
  const isSignupContextualized =
    signupContextualizedInfoExperiment.get('behaviour') === 'variant';
  useEffect(() => {
    const alertData = getAlertDataFromStorage(QUICK_ALERT_STORAGE_DATA);

    if (isUserLogged && alertData) {
      createAlert(alertData, IS_QUICK_ALERT_CREATION);
    }
  }, [isUserLogged]);

  const handleAlertCreation = () => {
    const quickAlertData = {
      ...getAlertFromFilters(cityId, filters),
      mediums: ['email']
    };

    if (isUserLogged) {
      createAlert(quickAlertData, IS_QUICK_ALERT_CREATION);
    } else {
      SoyuzLocalStorage.setItem(
        QUICK_ALERT_STORAGE_DATA,
        JSON.stringify(quickAlertData)
      );
      triggerOpenModal(isSignupContextualized);
    }
    onClick();
  };

  const className = classNames(
    'filter-header__results-button',
    'ga-search-form-my-alerts',
    {
      'filter-header__results-button--filters-collapsed': !expandedFilters
    },
    buttonClassName
  );

  return (
    <Button
      data-test={dataTest}
      onClick={handleAlertCreation}
      className={className}
      {...buttonProps}
    >
      {customText || trans('search.alert.button.save-my-search')}
    </Button>
  );
};

AlertButton.propTypes = {
  cityId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    'bathrooms[]': PropTypes.arrayOf(PropTypes.string),
    bed: PropTypes.string,
    budget: PropTypes.string,
    'features[]': PropTypes.arrayOf(PropTypes.string),
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    poi: PropTypes.string,
    'topology[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  isUserLogged: PropTypes.bool,
  expandedFilters: PropTypes.bool,
  onClick: PropTypes.func,
  buttonClassName: PropTypes.string,
  buttonProps: PropTypes.shape({}),
  customText: PropTypes.string,
  dataTest: PropTypes.string
};

AlertButton.defaultProps = {
  isUserLogged: false,
  expandedFilters: false,
  onClick: () => {},
  buttonClassName: '',
  buttonProps: {},
  customText: '',
  dataTest: 'alert-button'
};

export default AlertButton;
